import React, { useEffect, useState } from "react";
import styled from "styled-components";

import MeetingDetailsHeader from "./MeetingDetailsHeader";
import { useTranslate } from '@tolgee/react';
import TimePicker from "./TimePicker";
import { ReactComponent as RightArrow } from "../../Assets/icons/rightArrow.svg";
import { Eye, EyeOff, Link2Off, CalendarX, RefreshCw, Repeat } from "lucide-react";
import moment from "moment";
import copy from "copy-to-clipboard";

import EditMeetingParticipants from "./EditMeetingParticipants";
import _, { partial } from "lodash";
import MeetingDateTimePicker from "./MeetingDateTimePicker";
import RecurrenceFieldsCard from "./RecurrenceFieldsCard";
import DeleteConfirmationChild from "../GenericComponents/DeleteConfirmationChild";
import RecurrenceScopeModal from "../GenericComponents/RecurrenceScopeModal";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import PrimaryButtonRemove from "../GenericComponents/PrimaryButtonRemove";
import { useNotify } from "../../Helpers/Notifications";
import { buildMeetingJoinWebLink } from "../../Helpers/deepLinkHelper";
import { formatDateTimeWithTimeZone } from "../../Helpers/dateTimeHelper";
import { formatMeetingDisplayId } from "../../Helpers/meetingHelper";
import { getApiErrorMessage } from "../../Helpers/apiErrorMessage";
import { useAuthService } from "../../Context/AuthContext";
import { useMeetingJoinGrace } from "../../hooks/useMeetingJoinGrace";
import { useMeetingsService } from "../../Context/MeetingContext";
import {
  createDefaultRecurrencePattern,
  seriesToPattern,
  patternsEqual,
  formatRecurrenceCadence,
  patternToApiParams,
} from "../../Helpers/recurrenceHelper";
import { colors, rgb } from "../../Assets/styles/colors";

const MeetingDetailsCard = ({
  meeting,
  handleCloseMeetingDetails,
  handleDeleteMeetingBtn,
  handleUpdateMeetingDetails,
  handleRegeneratePasscode,
  handleJoinMeeting,
  contacts,
}) => {
  const { notifySuccess, notifyError, notifyInfo } = useNotify();
  const { user } = useAuthService();
  const { getRecurringMeeting, updateRecurringMeeting, cancelRecurringMeeting, getMeetings } = useMeetingsService();
  const [editParticipants, setEditParticipants] = useState(false);
  const [updateBtnDisabled, setUpdateBtnDisabled] = useState(true);

  const isRecurring = !!meeting?.recurring_meeting_series_id;
  const [recurrencePattern, setRecurrencePattern] = useState(() =>
    createDefaultRecurrencePattern(meeting?.start_at ? new Date(meeting.start_at) : new Date())
  );
  const [initialRecurrencePattern, setInitialRecurrencePattern] = useState(null);
  const [seriesLoaded, setSeriesLoaded] = useState(false);
  const [seriesStartDate, setSeriesStartDate] = useState(null);
  const [cancelScopeModalOpen, setCancelScopeModalOpen] = useState(false);
  const [saveScopeModalOpen, setSaveScopeModalOpen] = useState(false);
  const [savingRecurringScope, setSavingRecurringScope] = useState(false);

  const patternHasChanged =
    isRecurring && !!initialRecurrencePattern && !patternsEqual(recurrencePattern, initialRecurrencePattern);

  const isCurrentUserAdmin = !!meeting?.participants?.find(
    (participant) => participant.user.id === user?.id
  )?.admin;

  const { isPast, inGracePeriod, endedLabel, expiresLabel } = useMeetingJoinGrace(meeting);
  const isExpiredPast = isPast && !inGracePeriod;
  const isReadOnly = !!meeting?.read_only;
  const isLocked = isExpiredPast || isReadOnly;
  const hasMeetingStarted = meeting?.start_at && moment(meeting.start_at).isSameOrBefore(moment());

  const [title, setTitle] = useState(meeting?.title || "");
  const [description, setDescription] = useState(meeting?.description || "");
  const url = meeting?.url || "";
  const displayId = meeting?.display_id || "";
  const passcode = meeting?.passcode || "";
  const [showPasscode, setShowPasscode] = useState(false);

  const startDate = meeting?.start_at ? new Date(meeting.start_at) : new Date();
  const endDate = meeting?.end_at ? new Date(meeting.end_at) : new Date(startDate);

  const [meetingStartDate, setMeetingStartDate] = useState(startDate);
  const [meetingEndDate, setMeetingEndDate] = useState(endDate);

  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [initialParticipants, setInitialParticipants] = useState([]);

  const [otherParticipants, setOtherParticipants] = useState([]);
  const [initialOtherParticipants, setInitialOtherParticipants] = useState([]);
  const [deleteMeetingPopup, setDeleteMeetingPopup] = useState(false);
  const { t } = useTranslate();

  const now = new Date();
  const startDateClone = new Date(meetingStartDate);
  const isStartToday = startDateClone.toDateString() === now.toDateString();
  const minStartTime = isStartToday
    ? now
    : new Date(startDateClone.setHours(0, 0, 0, 0));

  const minEndDateTime =
    meetingEndDate.toDateString() === meetingStartDate.toDateString()
      ? new Date(Math.max(meetingStartDate.getTime(), now.getTime()))
      : now;

  const calculateMinEndTime = () => {
    const endDateClone = new Date(meetingEndDate);
    if (endDateClone.toDateString() === meetingStartDate.toDateString()) {
      return minEndDateTime;
    }
    if (endDateClone.toDateString() === now.toDateString()) {
      return now;
    }
    return null;
  };

  const membersArray = selectedParticipants.map((participant) =>
    participant.email
      ? `${participant.name.trim()} (${participant.email.trim()})`
      : participant.name.trim()
  );

  const allParticipants = membersArray.concat(
    otherParticipants.map((name) => name.trim())
  );

  const initializeData = () => {
    if (!meeting || !contacts || (!contacts.on_app_contacts && !contacts.length)) {
      return;
    }
    const callOtherParticipants = (meeting?.participants || [])
      .filter((participant) => !participant.admin)
      .map((participant) => participant.user);
    const onAppContacts = contacts.on_app_contacts || [];

    const foundParticipants = callOtherParticipants.filter((participant) =>
      onAppContacts.some((contact) => contact.id === participant.id)
    );

    // Filter participants NOT found in contacts_on_platform
    const notFoundParticipants = callOtherParticipants.filter(
      (participant) => !onAppContacts.some((contact) => contact.id === participant.id)
    );
    const allParticipants = foundParticipants.concat(notFoundParticipants);

    setSelectedParticipants(allParticipants);
    setInitialParticipants(allParticipants);

    setOtherParticipants([]);
    setInitialOtherParticipants([]);

    setTitle(meeting.title || "");
    setDescription(meeting.description || "");
    setMeetingStartDate(meeting?.start_at ? new Date(meeting.start_at) : new Date());
    setMeetingEndDate(meeting?.end_at ? new Date(meeting.end_at) : new Date());
  };

  const handleSelectParticipant = (participant) => {
    let found = selectedParticipants.find((item) => item.id === participant.id);

    if (found) {
      let filtered = selectedParticipants.filter(
        (item) => item.id != participant.id
      );
      setSelectedParticipants(filtered);
    } else {
      setSelectedParticipants((prev) => {
        let newList = [...prev, participant];
        let sorted = newList.sort((a, b) => (a.name > b.name ? 1 : -1));
        return sorted;
      });
    }
  };

  const handleSaveBtn = () => {
    setEditParticipants(false);
  };

  const handleBackBtn = () => {
    setSelectedParticipants(initialParticipants);
    setOtherParticipants(initialOtherParticipants);
    setEditParticipants(false);
  };

  const buildMeetingInviteText = () => {
    const organizerName =
      meeting?.organizer?.name || meeting?.organizer?.email || "";
    const formattedTime = formatDateTimeWithTimeZone(meeting?.start_at);

    const lines = [
      `${organizerName} ${t('meetings.invite_text_is_inviting_you')}`,
      "",
      `${t('meetings.invite_text_topic')}: ${meeting?.title || ""}`,
      `${t('meetings.invite_text_time')}: ${formattedTime}`,
      "",
      t('meetings.invite_text_join_meeting'),
      url,
    ];

    if (displayId) {
      lines.push("", `${t('meetings.meeting_id')}: ${formatMeetingDisplayId(displayId)}`);
    }
    if (passcode) {
      lines.push(`${t('meetings.passcode')}: ${passcode}`);
    }
    if (meeting?.join_token) {
      lines.push(
        "",
        t('meetings.invite_text_join_by_code_link'),
        buildMeetingJoinWebLink(meeting.join_token)
      );
    }

    return lines.join("\n");
  };

  const handleShareBtn = () => {
    copy(buildMeetingInviteText());
    notifySuccess(t('misc.link_copied'));
  };

  const checkUpdateBtnDisabled = () => {
    const titleChanged = title !== meeting.title;
    const descriptionChanged = description !== meeting.description;

    const selectedIds = new Set(selectedParticipants.map((p) => String(p.id)));
    const initialids = new Set(initialParticipants.map((p) => String(p.id)));

    const contactChanged = !_.isEqual(selectedIds, initialids);
    const startDateChanged = !moment(meetingStartDate).isSame(startDate);
    const endDateChanged = !moment(meetingEndDate).isSame(endDate);

    const otherParticipantChanged = !_.isEqual(
      otherParticipants,
      initialOtherParticipants
    );

    if (
      titleChanged ||
      descriptionChanged ||
      contactChanged ||
      patternHasChanged ||
      otherParticipantChanged ||
      endDateChanged ||
      startDateChanged
    ) {
      setUpdateBtnDisabled(false);
    } else {
      setUpdateBtnDisabled(true);
    }
  };

  const checkOrganizer = (organizer) => {
    if (!organizer) {
      return "";
    }

    let displayOrganizer = "";
    if (organizer.name) {
      displayOrganizer = organizer.name;
      if (organizer.email) {
        displayOrganizer = organizer.name + ` (${organizer.email})`;
      }
    } else {
      if (organizer.email) {
        displayOrganizer = organizer.email;
      }
    }

    return displayOrganizer;
  };

  function auto_grow(element) {
    element.style.height = "5px";
    element.style.height = element.scrollHeight + "px";
  }

  useEffect(() => {
    let element = document.getElementById("resizeTa-4");
    if (element) {
      auto_grow(element);
    }
  }, [description]);

  useEffect(() => {
    initializeData();
  }, [meeting, contacts]);

  useEffect(() => {
    if (!meeting?.recurring_meeting_series_id) {
      setSeriesLoaded(false);
      setInitialRecurrencePattern(null);
      setSeriesStartDate(null);
      return;
    }

    setSeriesLoaded(false);
    setSeriesStartDate(null);
    getRecurringMeeting(
      meeting.recurring_meeting_series_id,
      (response) => {
        const series = response.data.recurring_meeting_series;
        const pattern = seriesToPattern(series);
        setRecurrencePattern(pattern);
        setInitialRecurrencePattern(pattern);
        setSeriesStartDate(series.first_occurrence_start_at ? new Date(series.first_occurrence_start_at) : null);
        setSeriesLoaded(true);
      },
      () => {
        setSeriesLoaded(false);
      }
    );
  }, [meeting?.recurring_meeting_series_id, getRecurringMeeting]);

  useEffect(() => {
    checkUpdateBtnDisabled();
  }, [
    selectedParticipants,
    initialOtherParticipants,
    initialParticipants,
    otherParticipants,
    title,
    description,
    startDate,
    endDate,
    recurrencePattern,
    initialRecurrencePattern,
  ]);

  useEffect(() => {
    // Create new Date objects representing only the date part of the start and end dates
    const startDate = new Date(meetingStartDate);
    startDate.setHours(0, 0, 0, 0); // Set time to 00:00:00 for comparison

    const endDate = new Date(meetingEndDate);
    endDate.setHours(0, 0, 0, 0); // Set time to 00:00:00 for comparison

    const startTime = meetingStartDate.getTime(); // Keep the full start date and time
    const endTime = meetingEndDate.getTime(); // Keep the full end date and time

    // Case 1: If the dates are different (endDate is earlier), set the meeting end date to the start date
    if (endDate < startDate) {
      setMeetingEndDate(new Date(meetingStartDate));
    }

    // Case 2: If dates are the same but end time is earlier than start time, add a buffer
    if (endDate.getTime() === startDate.getTime() && endTime < startTime) {
      const updatedEndDate = new Date(meetingStartDate);
      updatedEndDate.setMinutes(meetingStartDate.getMinutes() + 15); // Add a 5-minute buffer
      setMeetingEndDate(updatedEndDate);
    }
  }, [meetingStartDate, meetingEndDate]);

  const buildRecurringUpdatePayload = () => {
    const payload = {
      title,
      description,
      participants_ids: selectedParticipants.map((participant) => participant.id),
      other_participants: otherParticipants,
      occurrence_id: meeting.id,
      start_at: meetingStartDate.toISOString(),
      end_at: meetingEndDate.toISOString(),
    };

    if (patternHasChanged) {
      Object.assign(payload, patternToApiParams(recurrencePattern));
    }

    return payload;
  };

  const handleSaveRecurringMeeting = (scope) => {
    if (scope === "single_occurrence") {
      setSaveScopeModalOpen(false);
      handleUpdateMeetingDetails(
        meeting.id,
        title,
        description,
        selectedParticipants,
        otherParticipants,
        meetingStartDate,
        meetingEndDate
      );
      return;
    }

    if (savingRecurringScope) return;
    setSavingRecurringScope(true);

    updateRecurringMeeting(
      meeting.recurring_meeting_series_id,
      { ...buildRecurringUpdatePayload(), scope },
      () => {
        setSavingRecurringScope(false);
        setSaveScopeModalOpen(false);
        getMeetings({}, () => {}, () => {});
        handleCloseMeetingDetails();
      },
      (error) => {
        setSavingRecurringScope(false);
        notifyError(getApiErrorMessage(error, t));
      }
    );
  };

  const handleCancelRecurringMeeting = (scope) => {
    if (savingRecurringScope) return;
    setSavingRecurringScope(true);

    cancelRecurringMeeting(
      meeting.recurring_meeting_series_id,
      { scope, occurrence_id: meeting.id },
      () => {
        setSavingRecurringScope(false);
        setCancelScopeModalOpen(false);
        getMeetings({}, () => {}, () => {});
        handleCloseMeetingDetails();
      },
      (error) => {
        setSavingRecurringScope(false);
        notifyError(getApiErrorMessage(error, t));
      }
    );
  };

  const saveScopeOptions = patternHasChanged
    ? [
        {
          value: "single_occurrence",
          label: t("meetings.scope_label_single_occurrence"),
          description: t("meetings.save_scope_disabled_single_occurrence"),
          disabled: true,
        },
        {
          value: "series_from_date",
          label: t("meetings.scope_label_series_from_date"),
          description: t("meetings.save_scope_disabled_series_from_date"),
          disabled: true,
        },
        {
          value: "entire_series",
          label: t("meetings.scope_label_entire_series"),
          description: t("meetings.save_scope_desc_entire_series"),
        },
      ]
    : [
        {
          value: "single_occurrence",
          label: t("meetings.scope_label_single_occurrence"),
          description: t("meetings.save_scope_desc_single_occurrence"),
        },
        {
          value: "series_from_date",
          label: t("meetings.scope_label_series_from_date"),
          description: t("meetings.save_scope_desc_series_from_date"),
        },
        {
          value: "entire_series",
          label: t("meetings.scope_label_entire_series"),
          description: t("meetings.save_scope_desc_entire_series"),
        },
      ];

  const cancelScopeOptions = [
    {
      value: "single_occurrence",
      label: t("meetings.scope_label_single_occurrence"),
      description: t("meetings.cancel_scope_desc_single_occurrence"),
    },
    {
      value: "series_from_date",
      label: t("meetings.scope_label_series_from_date"),
      description: t("meetings.cancel_scope_desc_series_from_date"),
    },
    {
      value: "entire_series",
      label: t("meetings.scope_label_entire_series"),
      description: t("meetings.cancel_scope_desc_entire_series"),
    },
  ];

  return (
    <ChatDetailsWrapper>
      <MeetingDetailsHeader
        handleClose={handleCloseMeetingDetails}
        editParticipants={editParticipants}
        handleSave={() => {
          if (isRecurring) {
            setSaveScopeModalOpen(true);
            return;
          }
          handleUpdateMeetingDetails(
            meeting.id,
            title,
            description,
            selectedParticipants,
            otherParticipants,
            meetingStartDate,
            meetingEndDate
          );
        }}
        infoChanged={!updateBtnDisabled}
      />

      {editParticipants && (
        <EditMeetingParticipants
          meeting={meeting}
          contacts={contacts}
          selectedParticipants={selectedParticipants}
          setSelectedParticipants={setSelectedParticipants}
          initialParticipants={initialParticipants}
          setInitialParticipants={setInitialParticipants}
          otherParticipants={otherParticipants}
          setOtherParticipants={setOtherParticipants}
          initialOtherParticipants={initialOtherParticipants}
          setInitialOtherParticipants={setInitialOtherParticipants}
          handleBackBtn={handleBackBtn}
          handleSaveBtn={handleSaveBtn}
          handleSelectParticipant={handleSelectParticipant}
        />
      )}
      <Wrapper>
        <Group>
          <OptionWrapper>
            <TitleRow>
              <GrowingNameInput
                value={title}
                placeholder={t('new_chat.title')}
                onChange={(e) => setTitle(e.currentTarget.value)}
                name="meetingTitle"
                maxLength={100}
                disabled={isLocked}
                style={isLocked ? { opacity: 0.5 } : undefined}
              ></GrowingNameInput>
              {isRecurring && (
                <RecurringBadge>
                  <Repeat size={11} />
                  {t('meetings.recurring')}
                </RecurringBadge>
              )}
            </TitleRow>
            {isRecurring && meeting.recurring_meeting_series_summary && (
              <RecurringCadenceText>
                {formatRecurrenceCadence(t, meeting.recurring_meeting_series_summary)}
                {meeting.series_occurrence_index && meeting.recurring_meeting_series_summary.total_occurrences
                  ? ` · ${t('meetings.occurrence_position', { index: meeting.series_occurrence_index, total: meeting.recurring_meeting_series_summary.total_occurrences })}`
                  : ""}
              </RecurringCadenceText>
            )}
            <Separator />
            <CustomTextArea
              id="resizeTa-4"
              value={description}
              placeholder={t('general.details')}
              onChange={(e) => setDescription(e.currentTarget.value)}
              name="meetingDetails"
              maxLength={1000}
              disabled={isLocked}
              style={isLocked ? { opacity: 0.5 } : undefined}
            ></CustomTextArea>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <DateTimeWrapper>
              <DateInfo>{t('new_chat.starts')} </DateInfo>
              <MeetingDateTimePicker
                slim={true}
                format={"dd-MM-yyyy"}
                value={meetingStartDate}
                setValue={setMeetingStartDate}
                noCalendar
                disabled={isLocked}
              />
              <TimePicker
                date={meetingStartDate}
                setDate={setMeetingStartDate}
                minTime={minStartTime}
                minDateTime={now}
                readOnly={isLocked}
              />
            </DateTimeWrapper>
            <Separator />
            <DateTimeWrapper>
              <DateInfo>{t('new_chat.ends')}</DateInfo>
              <MeetingDateTimePicker
                slim={true}
                format={"dd-MM-yyyy"}
                value={meetingEndDate}
                setValue={setMeetingEndDate}
                noCalendar
                disabled={isLocked}
              />
              <TimePicker
                date={meetingEndDate}
                setDate={setMeetingEndDate}
                minTime={calculateMinEndTime()}
                minDateTime={minEndDateTime}
                readOnly={isLocked}
              />
            </DateTimeWrapper>
          </OptionWrapper>
        </Group>
        {isRecurring && seriesLoaded && (
          <Group>
            <RecurrenceFieldsCard
              startDate={seriesStartDate || meetingStartDate}
              pattern={recurrencePattern}
              onChange={setRecurrencePattern}
              fieldsLocked={isExpiredPast}
              lockedMessage={isExpiredPast ? t('meetings.meeting_has_ended') : undefined}
            />
          </Group>
        )}
        <Group>
          <OptionWrapper
            onClick={() => !isLocked && setEditParticipants(true)}
            style={{ cursor: isLocked ? "default" : "pointer" }}
          >
            <InvitesButton>
              {t('meetings.invitees')}
              <p>{allParticipants.length}</p>
              <RightArrow />
            </InvitesButton>
            <ParticipantsContainer>
              {allParticipants.map((item, index) => (
                <InvitedPeople key={index}>
                  <p>{item}</p>
                </InvitedPeople>
              ))}
            </ParticipantsContainer>
          </OptionWrapper>
        </Group>
        <Group>
          <OptionWrapper>
            <OrganizerWrapper>
              <OrganizerTitle>
                {t('meetings.organizer')}
              </OrganizerTitle>
              <OrganizerText>{checkOrganizer(meeting.organizer)}</OrganizerText>
            </OrganizerWrapper>
          </OptionWrapper>
        </Group>

        <Group>
          <OptionWrapper style={{ flexDirection: "row" }}>
            <CustomNameInput
              value={url}
              readOnly
              name="meetingUrl"
              maxLength={100}
              style={isExpiredPast ? { opacity: 0.5 } : undefined}
            ></CustomNameInput>
            {isExpiredPast ? (
              <ExpiredTag>
                <Link2Off size={14} />
                {t('meetings.expired')}
              </ExpiredTag>
            ) : (
              <ShareBtn onClick={handleShareBtn}>
                {t('meetings.copy_invitation')}
              </ShareBtn>
            )}
          </OptionWrapper>
        </Group>
        {displayId && (
          <Group>
            <IdCodeCards>
              <IdCodeCard>
                <IdCodeLabel>{t('meetings.meeting_id')}</IdCodeLabel>
                <IdCodeValue>{displayId}</IdCodeValue>
              </IdCodeCard>
              <IdCodeCard>
                <IdCodeCardHeader>
                  <IdCodeLabel>{t('meetings.passcode')}</IdCodeLabel>
                  <IdCodeHeaderActions>
                    {!isLocked && (
                      <EyeToggleBtn
                        onClick={() => handleRegeneratePasscode(meeting.id)}
                        disabled={hasMeetingStarted || !isCurrentUserAdmin}
                        title={
                          !isCurrentUserAdmin
                            ? t('meetings.regenerate_passcode_organizer_only')
                            : hasMeetingStarted
                              ? t('meetings.regenerate_passcode_disabled')
                              : t('meetings.regenerate_passcode')
                        }
                      >
                        <RefreshCw size={14} />
                      </EyeToggleBtn>
                    )}
                    <EyeToggleBtn
                      onClick={() => setShowPasscode((prev) => !prev)}
                      title={
                        showPasscode
                          ? t('meetings.hide_passcode')
                          : t('meetings.show_passcode')
                      }
                    >
                      {showPasscode ? <EyeOff size={14} /> : <Eye size={14} />}
                    </EyeToggleBtn>
                  </IdCodeHeaderActions>
                </IdCodeCardHeader>
                <IdCodeValue>
                  {showPasscode ? passcode : "•".repeat(passcode.length)}
                </IdCodeValue>
              </IdCodeCard>
            </IdCodeCards>
          </Group>
        )}
        {isExpiredPast ? (
          <Group>
            <EndedBanner>
              <EndedBannerIcon>
                <CalendarX size={18} />
              </EndedBannerIcon>
              <EndedBannerText>
                <EndedBannerTitle>{t('meetings.meeting_has_ended')}</EndedBannerTitle>
                <EndedBannerSubtitle>{moment(meeting?.end_at).fromNow()}</EndedBannerSubtitle>
              </EndedBannerText>
            </EndedBanner>
          </Group>
        ) : (
          <>
            {inGracePeriod && (
              <Group>
                <GraceNotice>
                  {t('meetings.ended_ago', { time: endedLabel })} ·{" "}
                  {t('meetings.expires', { time: expiresLabel })}
                </GraceNotice>
              </Group>
            )}
            {isReadOnly && (
              <Group>
                <GraceNotice>{t('meetings.read_only_notice')}</GraceNotice>
              </Group>
            )}
            <Group>
              <PrimaryButton
                style={{ padding: "16px" }}
                onClick={() => handleJoinMeeting(meeting)}
              >
                {t('meetings.join_meeting')}
              </PrimaryButton>
            </Group>
            {isCurrentUserAdmin && !isReadOnly && (
              <Group>
                {isRecurring ? (
                  <PrimaryButtonRemove
                    style={{ padding: "16px" }}
                    onClick={() => setCancelScopeModalOpen(true)}
                  >
                    {t('meetings.cancel_meeting_confirm')}
                  </PrimaryButtonRemove>
                ) : (
                  <DeleteConfirmationChild
                    open={deleteMeetingPopup}
                    setOpen={setDeleteMeetingPopup}
                    variant={"delete_meeting"}
                    action={handleDeleteMeetingBtn}
                    item={meeting}
                  >
                    <PrimaryButtonRemove
                      style={{ padding: "16px" }}
                      onClick={() => setDeleteMeetingPopup(true)}
                    >
                      {t('general.delete')}
                    </PrimaryButtonRemove>
                  </DeleteConfirmationChild>
                )}
              </Group>
            )}
          </>
        )}
      </Wrapper>

      <RecurrenceScopeModal
        open={cancelScopeModalOpen}
        title={t('meetings.cancel_scope_title')}
        message={t('meetings.cancel_scope_message', { title: meeting?.title })}
        options={cancelScopeOptions}
        defaultValue="single_occurrence"
        confirmLabel={t('meetings.cancel_meeting_confirm')}
        danger
        loading={savingRecurringScope}
        onCancel={() => setCancelScopeModalOpen(false)}
        onConfirm={handleCancelRecurringMeeting}
      />

      <RecurrenceScopeModal
        open={saveScopeModalOpen}
        title={t('meetings.save_scope_title')}
        message={
          patternHasChanged
            ? t('meetings.save_scope_message', { title: meeting?.title })
            : t('meetings.save_scope_message_generic', { title: meeting?.title })
        }
        options={saveScopeOptions}
        defaultValue={patternHasChanged ? "entire_series" : "single_occurrence"}
        confirmLabel={t('meetings.save_changes')}
        loading={savingRecurringScope}
        onCancel={() => setSaveScopeModalOpen(false)}
        onConfirm={handleSaveRecurringMeeting}
      />
    </ChatDetailsWrapper>
  );
};

export default MeetingDetailsCard;

const ChatDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.chat.conversationBackground};
  height: 100%;
  z-index: 1;
`;

const ParticipantsContainer = styled.div`
  padding-bottom: 8px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 25px 21px;
  gap: 25px;
  overflow: auto;
  max-width: 631px;
  width: 100%;
  margin: 0 auto;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Separator = styled.div`
  width: 100%;
  min-height: 1px;
  background: ${({ theme }) => theme.sidebar.borderColor};
`;

const CustomInput = styled.input`
  background: none;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.40799999237060547px;
  width: 100%;
  padding: 15px 16px;
  &:focus-visible {
    outline: none;
  }
`;

const CustomNameInput = styled(CustomInput)`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;
const DateTimeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
`;

const DateInfo = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const InvitesButton = styled.button`
  display: flex;
  align-items: center;
  border-radius: 5px;
  border: none;
  padding: 14px 16px;
  cursor: pointer;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  font-size: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  gap: 10px;
  p {
    margin-left: auto;
  }
`;
const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 4px;
`;

const InvitedPeople = styled.div`
  overflow: hidden;
  display: flex;
  width: 100%;
  padding: 4px 16px;
  p {
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: left;
  }
`;

const IdCodeCards = styled.div`
  display: flex;
  gap: 8px;
`;

const IdCodeCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 4px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
`;

const IdCodeCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IdCodeHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IdCodeLabel = styled.div`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const IdCodeValue = styled.div`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 18px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
`;

const EyeToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.menu.secondaryTextColor};

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ShareBtn = styled.button`
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: none;
  cursor: pointer;
  padding: 14px 15px 15px 0px;
  border-radius: 10px;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;

  &:hover {
    opacity: 0.7;
  }
`;

const CustomTextArea = styled.textarea`
  padding: 15px 16px;
  width: 100%;
  resize: none;
  background: none;
  min-height: 80px;
  border: none;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  max-height: 250px;
  &:focus-visible {
    outline: none;
  }
`;
const OrganizerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const OrganizerText = styled.p`
  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
  padding: 10px 16px;
`;
const ExpiredTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 14px 15px 15px 0px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
`;

const EndedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
`;

const EndedBannerIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ theme }) => theme.sidebar.background};
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const EndedBannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EndedBannerTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const EndedBannerSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const GraceNotice = styled.div`
  padding: 0 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 13px;
  font-weight: 400;
`;

const OrganizerTitle = styled.p`
  line-height: 20px;
  font-size: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 500;
  padding: 14px 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 16px;
`;

const GrowingNameInput = styled(CustomNameInput)`
  flex: 1;
  min-width: 0;
`;

const RecurringBadge = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 5px;
  width: fit-content;
  background: rgba(${rgb(colors.emotiiPurple)}, 0.16);
  color: ${({ theme }) => theme.menu.selectedTextColor};
  border-radius: 20px;
  padding: 4px 10px 4px 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1px;
`;

const RecurringCadenceText = styled.p`
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  padding: 0 16px 12px 16px;
`;
